const strings = {
  en: {
    sectionLabel: 'Legal',
    title: 'Privacy Policy',
    subtitle: 'This Privacy Policy explains how SAPOT-AI collects, uses, and protects your information.',
    lastUpdated: 'Last updated: July 24, 2026',
    s1Title: '1. Introduction',
    s1p1: 'This Privacy Policy describes how SAPOT-AI ("we," "our," or "us") collects, uses, and discloses information when you use our web and mobile application and related services ("Service").',
    s1p2: 'By using the Service, you agree to the collection and use of information in accordance with this Privacy Policy.',
    s2Title: '2. Information We Collect',
    s2h1: '2.1 Personal Information',
    s2p1: 'We may collect personal information that you voluntarily provide, including but not limited to:',
    s2l1: ['Full name', 'Email address', 'Phone number', 'Profile information', 'Payment details (if applicable)'],
    s2h2: '2.2 Automatically Collected Information',
    s2p2: 'When you use the app, we may automatically collect:',
    s2l2: ['Device type and operating system', 'Unique device identifiers', 'IP address', 'App usage data and logs', 'Crash reports and diagnostics'],
    s3Title: '3. How We Use Your Information',
    s3p1: 'We use the collected information for the following purposes:',
    s3l1: [
      'To provide and maintain the Service',
      'To personalize user experience',
      'To communicate with you, including support and updates',
      'To process payments and transactions',
      'To improve and optimize the app',
      'To comply with legal obligations',
    ],
    s4Title: '4. Sharing and Disclosure of Information',
    s4p1: 'We do not sell your personal data.',
    s4p2: 'We may share your information with:',
    s4l1: [
      'Trusted third-party service providers (hosting, analytics, payment processing)',
      'Legal authorities if required by law',
      'Business partners with your consent',
    ],
    s5Title: '5. Data Retention',
    s5p1: 'We retain personal information only for as long as necessary to fulfill the purposes outlined in this policy, unless a longer retention period is required or permitted by law.',
    s6Title: '6. Data Security',
    s6p1: 'We implement industry-standard security measures to protect your data. However, no method of transmission or storage is completely secure, and we cannot guarantee absolute security.',
    s7Title: '7. Cookies and Tracking Technologies',
    s7p1: 'The app may use cookies or similar technologies (e.g., SDKs, analytics tools) to collect usage information and improve performance. You can manage tracking permissions through your device settings.',
    s8Title: '8. Google User Data (Google Calendar and Google Sheets)',
    s8p1: 'Certain features of the Service allow you to connect your Google account so that SAPOT-AI can access specific Google services on your behalf. This section describes how we collect, use, store, and share information received from Google APIs.',
    s8h1: '8.1 What Google data we access',
    s8p2: 'If you choose to connect your Google account, we request only the minimum permissions needed for the features you enable:',
    s8l1: [
      'Google Calendar (calendar.events, calendar.freebusy): we read your free/busy availability to offer appointment times, and we create, update, or cancel calendar events for appointments booked through the Service. We do not use the content of your other calendar events.',
      'Google Sheets (spreadsheets): we read and write data only in the spreadsheets you select, to export or sync data from your SAPOT-AI account.',
    ],
    s8h2: '8.2 How we use Google user data',
    s8p3: 'We use Google user data solely to provide the user-facing features described above. We do not use Google user data for advertising purposes, we do not sell it, and we do not use it to train machine-learning or AI models. No humans read this data except with your explicit permission, when necessary for security or legal compliance, or when required to provide support that you request.',
    s8h3: '8.3 Storage, sharing, and retention',
    s8p4: 'Google access credentials (OAuth tokens) are stored securely on our servers and protected using the security measures described in Section 6. We do not share or transfer Google user data to third parties, except to trusted service providers strictly as necessary to operate the features described above, or where required by law. When you disconnect your Google account or delete your SAPOT-AI account, we delete the associated Google tokens and any Google user data we hold within 30 days.',
    s8h4: '8.4 Revoking access',
    s8p5: "You may disconnect your Google account at any time from within the Service, or revoke SAPOT-AI's access via your Google Account security settings at https://myaccount.google.com/permissions.",
    s8h5: '8.5 Limited Use disclosure',
    s8p6: "SAPOT-AI's use and transfer to any other app of information received from Google APIs will adhere to the Google API Services User Data Policy, including the Limited Use requirements.",
    s9Title: '9. Your Privacy Rights',
    s9p1: 'Depending on your jurisdiction, you may have the right to:',
    s9l1: [
      'Access your personal data',
      'Correct inaccurate data',
      'Request deletion of your data',
      'Withdraw consent at any time',
      'Object to data processing',
    ],
    s9p2: 'To exercise these rights, contact us at noreply@mampuai.com.',
    s10Title: "10. Children's Privacy",
    s10p1: 'Our Service is not intended for individuals under the age of 18. We do not knowingly collect personal data from children.',
    s11Title: '11. International Data Transfers',
    s11p1: 'Your information may be transferred to and processed in countries outside your jurisdiction. We ensure appropriate safeguards are in place for such transfers.',
    s12Title: '12. Changes to This Privacy Policy',
    s12p1: 'We may update this Privacy Policy from time to time. We will notify you of any changes by updating the "Last updated" date and, where appropriate, through the app.',
    s13Title: '13. Contact Us',
    s13p1: 'If you have any questions about this Privacy Policy, please contact us:',
  },
  zh: {
    sectionLabel: '法律',
    title: '隐私政策',
    subtitle: '本隐私政策阐述了 SAPOT-AI 如何收集、使用和保护您的信息。',
    lastUpdated: '最后更新：2026年7月24日',
    s1Title: '1. 引言',
    s1p1: '本隐私政策描述了 SAPOT-AI（以下简称"我们"）在您使用我们的网页和移动应用程序及相关服务（以下简称"服务"）时，如何收集、使用和披露信息。',
    s1p2: '使用本服务即表示您同意按照本隐私政策收集和使用信息。',
    s2Title: '2. 我们收集的信息',
    s2h1: '2.1 个人信息',
    s2p1: '我们可能会收集您自愿提供的个人信息，包括但不限于：',
    s2l1: ['姓名', '电子邮箱', '联系电话', '个人资料信息', '支付信息（如适用）'],
    s2h2: '2.2 自动收集的信息',
    s2p2: '当您使用本应用时，我们可能自动收集以下信息：',
    s2l2: ['设备类型与操作系统', '设备唯一标识符', 'IP 地址', '应用使用数据与日志', '崩溃报告与诊断信息'],
    s3Title: '3. 我们如何使用您的信息',
    s3p1: '我们将收集到的信息用于以下目的：',
    s3l1: [
      '提供并维护本服务',
      '为用户提供个性化体验',
      '与您沟通，包括提供支持和更新',
      '处理付款与交易',
      '改进并优化本应用',
      '履行法律义务',
    ],
    s4Title: '4. 信息的共享与披露',
    s4p1: '我们不会出售您的个人数据。',
    s4p2: '我们可能会将您的信息共享给：',
    s4l1: [
      '值得信赖的第三方服务提供商（主机托管、数据分析、支付处理）',
      '法律要求下的监管机构',
      '在获得您同意的前提下的商业合作伙伴',
    ],
    s5Title: '5. 数据保留',
    s5p1: '我们仅在实现本政策所述目的所必需的期间内保留个人信息，除非法律要求或允许更长的保留期限。',
    s6Title: '6. 数据安全',
    s6p1: '我们采用行业标准的安全措施来保护您的数据。然而，任何传输或存储方式都无法做到绝对安全，因此我们无法保证信息的绝对安全。',
    s7Title: '7. Cookie 与跟踪技术',
    s7p1: '本应用可能使用 Cookie 或类似技术（例如 SDK、分析工具）来收集使用信息并提升性能。您可以通过设备设置管理相关的跟踪权限。',
    s8Title: '8. Google 用户数据（Google Calendar 和 Google Sheets）',
    s8p1: '本服务的某些功能允许您连接 Google 帐户，以便 SAPOT-AI 代表您访问特定的 Google 服务。本节说明我们如何收集、使用、存储和分享从 Google API 接收的信息。',
    s8h1: '8.1 我们访问哪些 Google 数据',
    s8p2: '如果您选择连接 Google 帐户，我们只会请求您启用的功能所需的最低权限：',
    s8l1: [
      'Google Calendar (calendar.events, calendar.freebusy)：我们会读取您的空闲/忙碌状态以提供预约时间，并为通过本服务预订的预约创建、更新或取消日历活动。我们不会使用您其他日历活动的内容。',
      'Google Sheets (spreadsheets)：我们只会读取和写入您选择的电子表格中的数据，以导出或同步您的 SAPOT-AI 帐户数据。',
    ],
    s8h2: '8.2 我们如何使用 Google 用户数据',
    s8p3: '我们仅将 Google 用户数据用于提供上述面向用户的功能。我们不会将 Google 用户数据用于广告目的，不会出售这些数据，也不会使用这些数据训练机器学习或 AI 模型。除非获得您的明确许可、出于安全或法律合规需要，或为提供您请求的支持而必要，否则不会有人读取这些数据。',
    s8h3: '8.3 存储、分享和保留',
    s8p4: 'Google 访问凭据（OAuth tokens）会安全存储在我们的服务器上，并使用第 6 节所述的安全措施进行保护。除为运营上述功能而严格必要的可信服务提供商，或法律要求外，我们不会与第三方分享或转移 Google 用户数据。当您断开 Google 帐户连接或删除 SAPOT-AI 帐户时，我们会在 30 天内删除相关 Google token 以及我们持有的任何 Google 用户数据。',
    s8h4: '8.4 撤销访问权限',
    s8p5: '您可以随时在本服务内断开 Google 帐户连接，或通过 Google 帐户安全设置撤销 SAPOT-AI 的访问权限：https://myaccount.google.com/permissions。',
    s8h5: '8.5 Limited Use 披露',
    s8p6: 'SAPOT-AI 对从 Google API 接收的信息的使用以及向任何其他应用转移该信息的行为，将遵守 Google API Services User Data Policy，包括 Limited Use 要求。',
    s9Title: '9. 您的隐私权利',
    s9p1: '根据您所在的司法管辖区，您可能享有以下权利：',
    s9l1: [
      '访问您的个人数据',
      '更正不准确的数据',
      '请求删除您的数据',
      '随时撤回同意',
      '反对数据处理',
    ],
    s9p2: '如需行使上述权利，请通过 noreply@mampuai.com 与我们联系。',
    s10Title: '10. 儿童隐私',
    s10p1: '本服务不面向未满 18 周岁的个人。我们不会故意收集儿童的个人数据。',
    s11Title: '11. 跨境数据传输',
    s11p1: '您的信息可能会被传输至您所在司法管辖区以外的国家并在当地进行处理。对于此类传输，我们将确保采取适当的保护措施。',
    s12Title: '12. 本隐私政策的变更',
    s12p1: '我们可能会不时更新本隐私政策。如有变更，我们将通过更新"最后更新日期"以及在适当情况下通过应用内通知的方式告知您。',
    s13Title: '13. 联系我们',
    s13p1: '如您对本隐私政策有任何疑问，请通过以下方式与我们联系：',
  },
};

export default strings;
